<template>
  <span @click="openModal" class="acc-btn">
    {{ button }}
  </span>

  <!-- OVERLAY -->
  <div v-if="open" class="acc-overlay" @click="closeModal"></div>

  <!-- DRAWER -->
  <div class="acc-drawer" :class="{ open: open }" @click.stop>
    <div>
      <div class="acc-header flex items-center justify-between gap-5">
        <h2 class="acc-title text-xl font-semibold">Accessibility Settings</h2>

        <!-- CLOSE BUTTON -->
        <button class="text-darkBlue" @click="closeModal">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="29"
            height="29"
            viewBox="0 0 29 29"
            fill="none"
          >
            <path
              d="M21.5 7.39355L7.39356 21.5"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <path
              d="M21.5 21.5L7.39356 7.39356"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>

      <div class="acc-options flex flex-wrap">
        <button
          class="acc-option-btn"
          v-for="option in options"
          :key="option.value"
          @click="toggleOption(option.value)"
          :class="{ active: isActive(option.value) }"
        >
          <span class="acc-option-icon" v-html="option.icon"></span>
          {{ option.label }}
        </button>
      </div>

      <div class="acc-footer flex flex-wrap items-center">
        <button
          class="btn-secondary-alt btn-big link dib"
          @click="resetSettings"
        >
          Reset Settings
        </button>

        <a
          href="/pages/accessibility-statement"
          class="btn-secondary-alt btn-big link dib"
        >
          Accessibility Statement
        </a>
      </div>
    </div>
  </div>
</template>

<script>
const zoomPlus = `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor"  viewBox="0 0 512 512"><path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376C296.3 401.1 253.9 416 208 416 93.1 416 0 322.9 0 208S93.1 0 208 0 416 93.1 416 208zM208 112c-13.3 0-24 10.7-24 24l0 48-48 0c-13.3 0-24 10.7-24 24s10.7 24 24 24l48 0 0 48c0 13.3 10.7 24 24 24s24-10.7 24-24l0-48 48 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-48 0 0-48c0-13.3-10.7-24-24-24z"/></svg>`;
const contrast = `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor"  viewBox="0 0 512 512"><path d="M448 256c0-106-86-192-192-192l0 384c106 0 192-86 192-192zM0 256a256 256 0 1 1 512 0 256 256 0 1 1 -512 0z"/></svg>`;
const font = `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 512 512"><path d="M285.1 50.7C279.9 39.3 268.5 32 256 32s-23.9 7.3-29.1 18.7L59.5 416 48 416c-17.7 0-32 14.3-32 32s14.3 32 32 32l88 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-6.1 0 22-48 208.3 0 22 48-6.1 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l88 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-11.5 0-167.4-365.3zM330.8 304L181.2 304 256 140.8 330.8 304z"/></svg>`;
const pause = `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor"  viewBox="0 0 512 512"><path d="M256 512a256 256 0 1 0 0-512 256 256 0 1 0 0 512zM224 192l0 128c0 17.7-14.3 32-32 32s-32-14.3-32-32l0-128c0-17.7 14.3-32 32-32s32 14.3 32 32zm128 0l0 128c0 17.7-14.3 32-32 32s-32-14.3-32-32l0-128c0-17.7 14.3-32 32-32s32 14.3 32 32z"/></svg>`;
const focus = `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 640 512"><path d="M176 56c0-13.3-10.7-24-24-24s-24 10.7-24 24l0 38.1-63-63c-9.4-9.4-24.6-9.4-33.9 0S21.7 55.6 31 65l63 63-38.1 0c-13.3 0-24 10.7-24 24s10.7 24 24 24l96 0c13.3 0 24-10.7 24-24l0-96zm0 400l0-96c0-13.3-10.7-24-24-24l-96 0c-13.3 0-24 10.7-24 24s10.7 24 24 24l38.1 0-63 63c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l63-63 0 38.1c0 13.3 10.7 24 24 24s24-10.7 24-24zm312 24c13.3 0 24-10.7 24-24l0-38.1 63 63c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-63-63 38.1 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-96 0c-13.3 0-24 10.7-24 24l0 96c0 13.3 10.7 24 24 24zM464 56l0 96c0 13.3 10.7 24 24 24l96 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-38.1 0 63-63c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-63 63 0-38.1c0-13.3-10.7-24-24-24s-24 10.7-24 24zM320 120c-57.3 0-99.4 34-125.6 64.3-20 23.1-32.4 45.8-37.6 56.1-2.5 5-4.9 9.7-4.9 15.6s2.3 10.6 4.9 15.6c5.2 10.3 17.6 33 37.6 56.1 26.2 30.3 68.2 64.3 125.6 64.3s99.4-34 125.6-64.3c20-23.1 32.4-45.8 37.6-56.1 2.5-5 4.9-9.7 4.9-15.6s-2.3-10.6-4.9-15.6c-5.2-10.3-17.6-33-37.6-56.1-26.2-30.3-68.2-64.3-125.6-64.3zM256 256a64 64 0 1 1 128 0 64 64 0 1 1 -128 0z"/></svg>`;
const accesibility = `<svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 512 512"
      fill="currentColor"
    >
      <path
        d="M0 256a256 256 0 1 1 512 0 256 256 0 1 1 -512 0zm161.5-86.1c-12.2-5.2-26.3 .4-31.5 12.6s.4 26.3 12.6 31.5l11.9 5.1c17.3 7.4 35.2 12.9 53.6 16.3l0 50.1c0 4.3-.7 8.6-2.1 12.6l-28.7 86.1c-4.2 12.6 2.6 26.2 15.2 30.4s26.2-2.6 30.4-15.2l24.4-73.2c1.3-3.8 4.8-6.4 8.8-6.4s7.6 2.6 8.8 6.4l24.4 73.2c4.2 12.6 17.8 19.4 30.4 15.2S339 397 334.8 384.4l-28.7-86.1c-1.4-4.1-2.1-8.3-2.1-12.6l0-50.1c18.4-3.5 36.3-8.9 53.6-16.3l11.9-5.1c12.2-5.2 17.8-19.3 12.6-31.5s-19.3-17.8-31.5-12.6L338.7 175c-26.1 11.2-54.2 17-82.7 17s-56.5-5.8-82.7-17l-11.9-5.1zM256 160a40 40 0 1 0 0-80 40 40 0 1 0 0 80z"
      />
    </svg>`;

export default {
  name: "Accessibility",
  props: {
    button: {
      type: String,
      default: "Accessibility Settings",
    },
  },
  data() {
    return {
      open: false,

      settings: {
        fontsize: 0, // 0,1,2
        contrast: 0,
        dyslexia: false,
        motion: false,
        focus: false,
      },

      options: [
        { label: "Increase Text Size", value: "fontsize", icon: zoomPlus },
        { label: "High Contrast Mode", value: "contrast", icon: contrast },
        { label: "Dyslexia-Friendly Font", value: "dyslexia", icon: font },
        { label: "Reduce Animations", value: "motion", icon: pause },
        { label: "Highlight Keyboard Focus", value: "focus", icon: focus },
      ],
      accesibilityIcon: accesibility,
    };
  },

  methods: {
    onToggleModal() {
      this.open = !this.open;
    },

    isActive(value) {
      if (value === "fontsize") return this.settings.fontsize > 0;
      return this.settings[value];
    },
    toggleOption(option) {
      switch (option) {
        case "fontsize":
          this.settings.fontsize = (this.settings.fontsize + 1) % 4;
          break;

        case "contrast":
          this.settings.contrast = (this.settings.contrast + 1) % 3;
          break;

        case "dyslexia":
          this.settings.dyslexia = !this.settings.dyslexia;

          break;
        case "motion":
        case "focus":
          this.settings[option] = !this.settings[option];
          break;
      }

      this.saveSettings();
      this.applyAll();
    },

    saveSettings() {
      localStorage.setItem("acc-settings", JSON.stringify(this.settings));
    },

    loadSettings() {
      const saved = localStorage.getItem("acc-settings");
      if (saved) this.settings = JSON.parse(saved);
    },

    applyAll() {
      const html = document.documentElement;

      // TEXT SIZE
      html.classList.remove("text-size-1", "text-size-2");
      if (this.settings.fontsize === 1) html.classList.add("text-size-1");
      if (this.settings.fontsize === 2) html.classList.add("text-size-2");

      // CONTRAST
      html.classList.remove("contrast-50", "contrast-150");
      if (this.settings.contrast === 1) html.classList.add("contrast-50");
      if (this.settings.contrast === 2) html.classList.add("contrast-150");

      html.classList.toggle("dyslexia-font", this.settings.dyslexia);
      html.classList.toggle("reduce-motion", this.settings.motion);
      html.classList.toggle("focus-highlight", this.settings.focus);
    },

    resetSettings() {
      this.settings = {
        fontsize: 0,
        contrast: 0,
        dyslexia: false,
        motion: false,
        focus: false,
      };

      localStorage.removeItem("acc-settings");

      this.applyAll();
    },
    openModal() {
      this.open = true;
      document.documentElement.style.overflow = "hidden";
    },

    closeModal() {
      this.open = false;
      document.documentElement.style.overflow = "";
    },
  },
  mounted() {
    this.loadSettings();

    // Browser settings
    if (window.matchMedia("(prefers-contrast: more)").matches) {
      this.settings.contrast = 2; // 150%
    }

    if (window.matchMedia("(prefers-contrast: less)").matches) {
      this.settings.contrast = 1; // 50%
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.settings.motion = true;
    }

    this.applyAll();
  },
};
</script>

<style>
:root {
  --acc-color: #2d2a262;
  --active-color: #fae0ce;
}

/* TEXT SIZE */
html.text-size-1 {
  font-size: 110% !important;
}
html.text-size-2,
.text-size-2 h2.picture-block-heading {
  font-size: 125% !important;
}
.text-size-1 .footer-menu a,
.text-size-1 .label,
.text-size-1 .pxs-announcement-bar {
  font-size: 17px !important;
}
.text-size-2 .footer-menu a,
.text-size-2 .label,
.text-size-2 .pxs-announcement-bar {
  font-size: 20px !important;
}

.text-size-1 h2.picture-block-heading {
  font-size: 74px !important;
}
.text-size-2 h2.picture-block-heading {
  font-size: 87px !important;
}
.text-size-1 .product .title a {
  font-size: 21px !important;
}
.text-size-2 .product .title a {
  font-size: 25px !important;
}
.text-size-1 .product-card-details h3.brand a,
.text-size-1 .product-card-interaction {
  font-size: 14px !important;
}
.text-size-2 .product-card-details h3.brand a,
.text-size-2 .product-card-interaction {
  font-size: 16px !important;
}

.text-size-1 h2.section-title {
  font-size: 18px !important;
}
.text-size-2 h2.section-title {
  font-size: 22px !important;
}

/* HIGH CONTRAST */
html.contrast-50 {
  filter: contrast(50%) !important;
}

html.contrast-150 {
  filter: contrast(150%) !important;
}

/* DYSLEXIA-FRIENDLY FONT */
html.dyslexia-font,
.dyslexia-font body,
.dyslexia-font * {
  --accessible-font: "Dyslexic" !important;
  font-family: "Dyslexic", sans-serif !important;
}

/* REDUCE ANIMATIONS */
html.reduce-motion * {
  animation: none !important;
  transition: none !important;
  scroll-behavior: auto !important;
}

/* FOCUS HIGHLIGHT */
html.focus-highlight *:focus {
  outline: 3px solid var(--acc-color) !important;
  outline-offset: 3px !important;
}

/* ui settings */
.acc-btn {
  cursor: pointer;
  color: #000;
  transition: all 0.3s ease;
}
.acc-btn:hover {
  color: #000000bf;
}
/* OVERLAY */
.acc-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  z-index: 9999999;
}

/* DRAWER */
.acc-drawer {
  position: fixed;
  width: 600px;
  max-width: 90%;
  height: 100vh;
  background: white;
  z-index: 99999000999;
  top: 0;
  right: -100%;
  transition: right 0.35s ease;
  overflow-y: auto;
  padding: 30px;
  @apply max-sm:w-full max-sm:max-w-full;
}

.acc-drawer.open {
  right: 0;
}
.acc-title {
  color: var(--acc-color);
}
.acc-slideout {
  padding: 30px;
}
.acc-header {
  margin-bottom: 40px;
}
.acc-header button {
  background: transparent;
  border: none;
  color: var(--acc-color);
}
.acc-floating-btn {
  position: fixed;
  bottom: 100px;
  left: 37px;
  z-index: 9999;
  width: 40px;
  height: 40px;
  background: #fff;
  padding: 2px;
  border-radius: 50%;
  color: var(--acc-color);
}
.acc-options {
  gap: 15px;
}
.acc-option-icon {
  width: 24px;
  display: block;
}

.acc-option-btn {
  width: 100%;
  padding: 14px 16px;
  background: #fff;

  border: 2px solid black;
  text-align: left;
  font-size: inherit;
  color: var(--acc-color);
  font-weight: 500;
  transition: all 0.25s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.acc-option-btn:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.acc-option-btn.active {
  border: 2px solid var(--active-color);

  background: #ffffff1c;
  box-shadow: 0 3px 10px rgba(75, 80, 86, 0.25);
}

.acc-option-btn:active {
  transform: scale(0.99);
}
.acc-footer {
  margin-top: 40px;
  gap: 10px;
  flex-wrap: wrap;
}
.acc-footer button,
.acc-footer a {
  flex: 1;
  max-width: 100%;
  padding: 15px 5px !important;
}
@media (max-width: 500px) {
  .acc-floating-btn {
    left: 29px;
  }
  .acc-drawer {
    width: 100%;
    max-width: 100%;
  }
  .acc-footer button,
  .acc-footer a {
    width: 100%;
    flex: 1 1 100%;
  }
}
</style>
